import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Users,
  Calendar,
  ClipboardList,
  Trophy,
  Clock,
  CheckCircle,
  UserCheck,
  Loader2,
  MapPin,
  RefreshCw,
  BookOpen,
  Bell,
  AlertTriangle,
  ChevronRight,
  FileText,
  Mail,
  Phone,
  Shirt,
  Hash,
  Layers,
  Receipt,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SessionPhotoUploadButton } from "@/components/SessionPhotoUploadButton";
import CoachTrials from "../components/CoachTrials";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [rosterModal, setRosterModal] = useState({ open: false, team: null, players: [] });
  const [loadingRoster, setLoadingRoster] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await axios.get(`${API}/academy/coach/dashboard`, {
        withCredentials: true,
      });
      setDashboardData(response.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError("Failed to load dashboard data");
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewTeamRoster = async (team) => {
    try {
      setLoadingRoster(true);
      setRosterModal({ open: true, team, players: [] });

      const response = await axios.get(
        `${API}/academy/coach/team/${team.id}/roster`,
        { withCredentials: true }
      );

      setRosterModal({
        open: true,
        team: response.data.team,
        players: response.data.players,
      });
    } catch (err) {
      console.error("Error fetching roster:", err);
      toast.error("Failed to load team roster");
    } finally {
      setLoadingRoster(false);
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axios.post(
        `${API}/academy/notifications/${notifId}/read`,
        {},
        { withCredentials: true }
      );
      setDashboardData((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          items: prev.notifications.items.map((n) =>
            n.id === notifId ? { ...n, read: true } : n
          ),
          unread_count: Math.max(0, prev.notifications.unread_count - 1),
        },
      }));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Unable to Load Dashboard</h3>
          <p className="text-slate-400 mb-4">{error || "Something went wrong"}</p>
          <Button onClick={() => fetchDashboard()}>Try Again</Button>
        </Card>
      </div>
    );
  }

  const { teams, term, session_templates, training_sessions, recent_reports, notifications, stats } = dashboardData;

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-5 px-4 sm:py-6 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:bg-white/20 min-h-[44px]">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Academy
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 min-h-[44px]"
              onClick={() => fetchDashboard(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">Coach Dashboard</h1>
          <p className="text-green-200 text-sm sm:text-base break-words">Welcome, Coach {user?.name || user?.email}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/handbook/coach" data-testid="coach-handbook-link">
              <Button size="sm" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 min-h-[44px]">
                <BookOpen className="w-4 h-4 mr-2" />
                Coach Handbook
              </Button>
            </Link>
            <Link to="/academy/coach-kit-handover" data-testid="coach-kit-handover-link">
              <Button size="sm" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 min-h-[44px]">
                <Shirt className="w-4 h-4 mr-2" />
                Kit Handover
              </Button>
            </Link>
            <Link to="/academy/coach/kit-numbers" data-testid="coach-kit-numbers-link">
              <Button size="sm" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 min-h-[44px]">
                <Hash className="w-4 h-4 mr-2" />
                Kit Numbers
              </Button>
            </Link>
            <Link to="/academy/fixtures" data-testid="coach-fixtures-link">
              <Button size="sm" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 min-h-[44px]">
                <Trophy className="w-4 h-4 mr-2" />
                Fixtures
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-5 sm:p-6">
        <CoachTrials />
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Trophy className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_teams || 0}</p>
              <p className="text-sm text-slate-300">Teams</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Users className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_players || 0}</p>
              <p className="text-sm text-slate-300">Players</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Calendar className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {stats?.total_sessions ?? session_templates?.length ?? 0}
              </p>
              <p className="text-sm text-slate-300">Sessions</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.sessions_pending_attendance || 0}</p>
              <p className="text-sm text-slate-300">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50">
            <CardContent className="pt-4 text-center">
              <Trophy className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats?.total_goals || 0}</p>
              <p className="text-sm text-slate-300">Team Goals</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Link to="/academy/coach/squads" className="block">
            <Card className="p-6 h-full hover:shadow-lg transition-shadow cursor-pointer bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20" data-testid="squads-card">
              <Layers className="w-8 h-8 text-emerald-400 mb-2" />
              <h3 className="font-semibold text-white">Squads & Buckets</h3>
              <p className="text-sm text-slate-300">Move players from the bucket into your squad</p>
            </Card>
          </Link>

          <Link to="/academy/attendance">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20" data-testid="attendance-card">
              <ClipboardList className="w-10 h-10 text-purple-400 mb-3" />
              <h3 className="font-bold text-lg text-white">Mark Attendance</h3>
              <p className="text-sm text-slate-300">Record session attendance</p>
              {stats?.sessions_pending_attendance > 0 && (
                <Badge className="mt-2 bg-red-500">{stats.sessions_pending_attendance} pending</Badge>
              )}
            </Card>
          </Link>
          <Link to="/academy/my-pay" data-testid="coach-my-pay-link">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20" data-testid="my-pay-card">
              <Receipt className="w-10 h-10 text-yellow-400 mb-3" />
              <h3 className="font-bold text-lg text-white">My Pay</h3>
              <p className="text-sm text-slate-300">Your sessions, and invoice the club</p>
            </Card>
          </Link>
          <Link to="/academy/player-feedback">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20" data-testid="feedback-card">
              <FileText className="w-10 h-10 text-amber-400 mb-3" />
              <h3 className="font-bold text-lg text-white">Player Feedback</h3>
              <p className="text-sm text-slate-300">Submit player assessments</p>
            </Card>
          </Link>
          <Link to="/academy/broadcast">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20" data-testid="broadcast-card">
              <Bell className="w-10 h-10 text-cyan-400 mb-3" />
              <h3 className="font-bold text-lg text-white">Send Broadcast</h3>
              <p className="text-sm text-slate-300">Message parents</p>
            </Card>
          </Link>
          <Link to="/academy/sessions">
            <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20" data-testid="sessions-card">
              <Calendar className="w-10 h-10 text-emerald-400 mb-3" />
              <h3 className="font-bold text-lg text-white">Manage Sessions</h3>
              <p className="text-sm text-slate-300">Create and schedule training</p>
            </Card>
          </Link>
          <Card 
            className="p-6 hover:shadow-lg transition-shadow cursor-pointer bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20" 
            data-testid="teams-card"
            onClick={() => teams?.length > 0 && viewTeamRoster(teams[0])}
          >
            <Trophy className="w-10 h-10 text-blue-400 mb-3" />
            <h3 className="font-bold text-lg text-white">View Roster</h3>
            <p className="text-sm text-slate-300">Team players & contacts</p>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* My Teams */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" /> My Teams
              </CardTitle>
            </CardHeader>
            <CardContent>
              {teams?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                  <p>No teams assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teams?.map((team) => (
                    <div
                      key={team.id}
                      className="p-4 bg-[#0a0f1a] rounded-lg hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium text-white">{team.name}</p>
                          <p className="text-sm text-slate-400">{team.age_group || "All ages"}</p>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400">
                          {team.player_count || 0} players
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-4 text-slate-400">
                          <span className="flex items-center gap-1">
                            <Trophy className="w-3 h-3 text-yellow-500" />
                            {team.total_goals || 0} goals
                          </span>
                          <span className="flex items-center gap-1">
                            <UserCheck className="w-3 h-3 text-blue-500" />
                            {team.total_assists || 0} assists
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-white border-slate-600 hover:bg-slate-700"
                        >
                          View Roster
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Weekly Schedule */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" /> Weekly Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              {session_templates?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No scheduled sessions</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {session_templates?.map((session, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-[#0a0f1a] rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-sm">{session.name}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                          <span className="font-medium text-emerald-400">
                            {session.day_of_week}
                          </span>
                          <span>•</span>
                          <span>
                            {session.start_time} - {session.end_time}
                          </span>
                          {session.age_group && (
                            <>
                              <span>•</span>
                              <Badge variant="outline" className="text-xs py-0">
                                {session.age_group}
                              </Badge>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {session.id && <SessionPhotoUploadButton sessionId={session.id} />}
                        {session.location && (
                          <div className="flex items-center text-xs text-slate-400">
                            <MapPin className="w-3 h-3 mr-1" />
                            {session.location}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sessions Pending Attendance */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" /> Pending Attendance
                {training_sessions?.pending_attendance?.length > 0 && (
                  <Badge className="bg-red-500/100 text-white">
                    {training_sessions.pending_attendance.length}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {training_sessions?.pending_attendance?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p>All attendance recorded!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {training_sessions?.pending_attendance?.map((session, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border-l-4 border-yellow-500/30"
                    >
                      <div>
                        <p className="font-medium text-sm">
                          {session.description || session.name || "Training Session"}
                        </p>
                        <p className="text-xs text-slate-400">
                          {session.session_date?.split("T")[0]} •{" "}
                          {session.start_time} - {session.end_time}
                        </p>
                      </div>
                      <Link to={`/academy/attendance/${session.id}`}>
                        <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700">
                          Mark
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" /> Notifications
                {notifications?.unread_count > 0 && (
                  <Badge className="bg-red-500/100 text-white">
                    {notifications.unread_count}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notifications?.items?.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications?.items?.slice(0, 5).map((notif, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        notif.read
                          ? "bg-[#0a0f1a]"
                          : "bg-emerald-500/10 border-l-4 border-emerald-500/30"
                      }`}
                      onClick={() => !notif.read && markNotificationRead(notif.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{notif.title}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(notif.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2">{notif.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Match Reports */}
        {recent_reports?.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" /> Recent Match Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {recent_reports?.slice(0, 4).map((report, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-[#0a0f1a] rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">
                        {report.type === "match" ? "Match vs " + (report.opponent_name || "Opponent") : "Training"}
                      </span>
                      {report.result && (
                        <Badge
                          className={
                            report.result === "win"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : report.result === "loss"
                              ? "bg-red-500/20 text-red-400"
                              : "bg-slate-800/50 text-white"
                          }
                        >
                          {report.result.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    {report.our_score !== undefined && (
                      <p className="text-lg font-bold">
                        {report.our_score} - {report.opponent_score}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(report.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Term Info */}
        {term && (
          <Card className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Current Term</p>
                  <p className="font-bold text-lg text-emerald-400">{term.name}</p>
                  <p className="text-sm text-slate-400">
                    {term.start_date?.split("T")[0]} to {term.end_date?.split("T")[0]}
                  </p>
                </div>
                <Link to="/academy/sessions">
                  <Button variant="outline" className="border-emerald-500/30 text-emerald-400">
                    View All Sessions
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Team Roster Modal */}
      <Dialog open={rosterModal.open} onOpenChange={(open) => setRosterModal({ ...rosterModal, open })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              {rosterModal.team?.name} - Roster
            </DialogTitle>
          </DialogHeader>

          {loadingRoster ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {rosterModal.players?.length === 0 ? (
                <p className="text-center text-slate-400 py-8">No players in this team</p>
              ) : (
                rosterModal.players?.map((player, idx) => (
                  <div key={idx} className="p-4 bg-[#0a0f1a] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">
                          {player.name || player.child_name}
                        </p>
                        <p className="text-sm text-slate-400">
                          {player.membership_number || "No ID"}
                        </p>
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        {player.age_group || "Unassigned"}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Trophy className="w-3 h-3 text-yellow-500" />
                        {player.goals_scored || 0} goals
                      </span>
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-3 h-3 text-blue-500" />
                        {player.assists || 0} assists
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-purple-500" />
                        {player.sessions_attended || 0} sessions
                      </span>
                    </div>

                    {player.parent_name && (
                      <div className="pt-2 border-t border-white/5 mt-2">
                        <p className="text-xs text-slate-400 mb-1">Parent Contact:</p>
                        <div className="flex flex-wrap gap-3 text-sm">
                          <span className="font-medium">{player.parent_name}</span>
                          {player.parent_email && (
                            <span className="flex items-center gap-1 text-blue-400">
                              <Mail className="w-3 h-3" />
                              {player.parent_email}
                            </span>
                          )}
                          {player.parent_phone && (
                            <span className="flex items-center gap-1 text-emerald-400">
                              <Phone className="w-3 h-3" />
                              {player.parent_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
